import { useMemo } from "react";
import { useSelector } from "react-redux";

import type { ReferencedValue, ValueTombstone } from "@skilitics/nautilus-core-designer-data";

import type { ResolvedValueSignature } from "./resolveValueSignature.ts";
import { useGetValueSubtypeDefinition } from "./useGetValueSubtypeDefinition.ts";
import { useResolveValueSignature } from "./useResolveValueSignature.ts";
import type { ValueSubtypeSpec } from "./valueSubtypeSpecs.ts";

const noSpec = () => undefined;

/**
 * A subject's signature with the spec its subtype derives from state joined on.
 * The declaration site supplies what varies per value (a dial's bounds); the
 * subtype supplies what lives in state (a locale's content languages). The
 * declaration wins field by field.
 *
 * A derived spec says how a value reads, never what is stored under it: writers
 * resolve a signature without a store, through `useResolveValueSignature`.
 */
export const useValueSignature = (
  subject: ReferencedValue | ValueTombstone | undefined,
): ResolvedValueSignature | undefined => {
  const resolveValueSignature = useResolveValueSignature();
  const getValueSubtypeDefinition = useGetValueSubtypeDefinition();

  const signature = subject && resolveValueSignature(subject);
  const derived: ValueSubtypeSpec | undefined = useSelector(
    (signature && getValueSubtypeDefinition(signature).specSelector) ?? noSpec,
  );

  return useMemo(
    () =>
      signature && derived
        ? { ...signature, subtypeSpec: { ...derived, ...signature.subtypeSpec } as ValueSubtypeSpec }
        : signature,
    [derived, signature],
  );
};
